import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../context/ThemeContext';
import { useToast } from '../../context/ToastContext';
import api, {
  timeEntriesAPI,
  projectsAPI,
  usersAPI,
  clientsAPI,
  sredCategoriesAPI,
} from '../../services/api';

interface ClockifyEntry {
  Project: string;
  Client: string;
  Description: string;
  Task: string;
  User: string;
  Email: string;
  Group: string;
  Tags: string;
  'Start Date': string;
  'Start Time': string;
  'End Date': string;
  'End Time': string;
  'Duration (h)': string;
  'Duration (decimal)': string;
  'Billable Rate (USD)': string;
  'Billable Amount (USD)': string;
}

interface ImportPreview {
  rowIndex: number;
  clockifyData: ClockifyEntry;
  mappedData: any;
  errors: string[];
  warnings: string[];
}

interface ClockifyImportProps {
  onClose: () => void;
  onImportComplete: () => void;
}

const ClockifyImport: React.FC<ClockifyImportProps> = ({
  onClose,
  onImportComplete,
}) => {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const { showToast } = useToast();
  const [file, setFile] = useState<File | null>(null);
  const [csvData, setCsvData] = useState<ClockifyEntry[]>([]);
  const [preview, setPreview] = useState<ImportPreview[]>([]);
  const [projects, setProjects] = useState<any[]>([]);
  const [clients, setClients] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [importing, setImporting] = useState(false);
  const [currentStep, setCurrentStep] = useState(1); // 1: Upload, 2: Preview, 3: Results
  const [importResults, setImportResults] = useState<any>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Mapping configuration
  const [mappingConfig, setMappingConfig] = useState({
    defaultProject: '',
    defaultCategory: '',
    userEmailMapping: true, // Map by email vs name
    billableByDefault: true,
  });

  React.useEffect(() => {
    loadReferenceData();
  }, []);

  const loadReferenceData = async () => {
    try {
      const [projectsRes, clientsRes, usersRes, categoriesRes] = await Promise.all([
        projectsAPI.getAll(),
        clientsAPI.getAll(),
        usersAPI.getAll(),
        sredCategoriesAPI.getAll(),
      ]);
      setProjects(projectsRes.data.projects || []);
      setClients(clientsRes.data.clients || []);
      setUsers(usersRes.data.users || []);
      setCategories(categoriesRes.data.sred_activity_categories || []);
    } catch (error) {
      console.error('Failed to load reference data:', error);
    }
  };

  const parseCSV = (text: string): ClockifyEntry[] => {
    const lines = text.split('\n');
    if (lines.length < 2) return [];

    const headers = lines[0].split(',').map((h) => h.trim().replace(/"/g, ''));
    const entries: ClockifyEntry[] = [];

    for (let i = 1; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) continue;

      const values: string[] = [];
      let currentValue = '';
      let inQuotes = false;

      for (let j = 0; j < line.length; j++) {
        const char = line[j];
        if (char === '"') {
          inQuotes = !inQuotes;
        } else if (char === ',' && !inQuotes) {
          values.push(currentValue.trim());
          currentValue = '';
        } else {
          currentValue += char;
        }
      }
      values.push(currentValue.trim());

      const entry: any = {};
      headers.forEach((header, index) => {
        entry[header] = values[index] || '';
      });
      entries.push(entry as ClockifyEntry);
    }

    return entries;
  };

  // Parse date string from Clockify (handles MM/DD/YYYY, DD/MM/YYYY, YYYY-MM-DD formats)
  const parseAndFormatDate = (dateStr: string): string | null => {
    if (!dateStr) return null;

    // Try parsing as ISO format first (YYYY-MM-DD)
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      return dateStr;
    }

    // Try MM/DD/YYYY or DD/MM/YYYY format
    const parts = dateStr.split('/');
    if (parts.length === 3) {
      const [first, second, third] = parts.map((p) => parseInt(p, 10));

      // If third part is 4 digits, it's the year
      if (third > 100) {
        // Assume MM/DD/YYYY (US format, which Clockify uses)
        const year = third;
        const month = first;
        const day = second;
        return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      }
    }

    // Fallback: try to parse with Date and format
    const parsed = new Date(dateStr);
    if (!isNaN(parsed.getTime())) {
      return parsed.toISOString().split('T')[0];
    }

    return dateStr;
  };

  // Combine date and time into ISO datetime string
  const combineDateTime = (dateStr: string, timeStr: string): string | null => {
    if (!dateStr || !timeStr) return null;

    const formattedDate = parseAndFormatDate(dateStr);
    if (!formattedDate) return null;

    // Handle time formats like "9:00 AM", "09:00", "9:00:00"
    let hours = 0;
    let minutes = 0;

    // Check for AM/PM format
    const ampmMatch = timeStr.match(
      /^(\d{1,2}):(\d{2})(?::\d{2})?\s*(AM|PM)?$/i,
    );
    if (ampmMatch) {
      hours = parseInt(ampmMatch[1], 10);
      minutes = parseInt(ampmMatch[2], 10);
      const ampm = ampmMatch[3]?.toUpperCase();

      if (ampm === 'PM' && hours !== 12) {
        hours += 12;
      } else if (ampm === 'AM' && hours === 12) {
        hours = 0;
      }
    }

    const timeFormatted = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:00`;
    return `${formattedDate}T${timeFormatted}`;
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const uploadedFile = event.target.files?.[0];
    if (!uploadedFile) return;

    if (!uploadedFile.name.endsWith('.csv')) {
      showToast(t('clockifyImport.uploadCSVFile'), 'warning');
      return;
    }

    setFile(uploadedFile);
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      try {
        const parsed = parseCSV(text);
        setCsvData(parsed);
        generatePreview(parsed);
      } catch (error) {
        showToast(t('clockifyImport.parseError'), 'error');
        console.error('CSV parsing error:', error);
      }
    };
    reader.readAsText(uploadedFile);
  };

  const generatePreview = (data: ClockifyEntry[]) => {
    const previewData: ImportPreview[] = data.map((entry, index) => {
      const errors: string[] = [];
      const warnings: string[] = [];

      // Find user by email or name
      let user = null;
      if (mappingConfig.userEmailMapping && entry.Email) {
        user = users.find(
          (u) => u.email.toLowerCase() === entry.Email.toLowerCase(),
        );
      }
      if (!user && entry.User) {
        user = users.find((u) =>
          u.full_name.toLowerCase().includes(entry.User.toLowerCase()),
        );
      }

      if (!user) {
        if (entry.Email) {
          warnings.push(`User will be created: ${entry.User} (${entry.Email})`);
        } else {
          errors.push(`User not found and no email provided: ${entry.User}`);
        }
      }

      // Find client (Clockify exports include a Client column)
      let client = null;
      if (entry.Client) {
        client = clients.find(
          (c) => c.name?.toLowerCase() === entry.Client.toLowerCase() ||
                 c.company?.toLowerCase() === entry.Client.toLowerCase(),
        );
        if (!client) {
          warnings.push(`Client will be created: ${entry.Client}`);
        }
      }

      // Find project
      let project = null;
      if (entry.Project) {
        project = projects.find(
          (p) => p.name.toLowerCase() === entry.Project.toLowerCase(),
        );
      }
      if (!project && mappingConfig.defaultProject) {
        project = projects.find(
          (p) => p.id.toString() === mappingConfig.defaultProject,
        );
      }
      if (!project && entry.Project) {
        warnings.push(`Project will be created: ${entry.Project}`);
      } else if (!project && !entry.Project) {
        errors.push('No project name provided');
      }

      // Validate dates
      const startDate = entry['Start Date'];
      const endDate = entry['End Date'] || entry['Start Date'];
      if (!startDate || !Date.parse(startDate)) {
        errors.push('Invalid start date');
      }

      // Parse duration
      let hoursWorked = 0;
      if (entry['Duration (decimal)']) {
        hoursWorked = parseFloat(entry['Duration (decimal)']);
      } else if (entry['Duration (h)']) {
        // Parse format like "2:30" to decimal hours
        const duration = entry['Duration (h)'];
        const parts = duration.split(':');
        if (parts.length === 2) {
          hoursWorked = parseInt(parts[0]) + parseInt(parts[1]) / 60;
        }
      }

      if (hoursWorked <= 0) {
        warnings.push('No valid duration found');
      }

      // Combine date and time into proper datetime strings
      const startDateTime =
        startDate && entry['Start Time']
          ? combineDateTime(startDate, entry['Start Time'])
          : null;
      const endDateTime =
        (endDate || startDate) && entry['End Time']
          ? combineDateTime(endDate || startDate, entry['End Time'])
          : null;

      const mappedData = {
        project_id: project?.id,
        user_id: user?.id,
        sred_activity_category_id:
          mappingConfig.defaultCategory || categories[0]?.id,
        entry_date: parseAndFormatDate(startDate),
        start_time: startDateTime,
        end_time: endDateTime,
        hours_worked: hoursWorked,
        description:
          entry.Description || entry.Task || 'Imported from Clockify',
        is_billable: mappingConfig.billableByDefault,
        is_sred_eligible: false,
        is_cdae_eligible: false,
      };

      return {
        rowIndex: index + 2, // +2 because CSV is 1-indexed and has header
        clockifyData: entry,
        mappedData,
        errors,
        warnings,
      };
    });

    setPreview(previewData);
    setCurrentStep(2);
  };

  const performImport = async () => {
    setImporting(true);
    const results = {
      successful: 0,
      failed: 0,
      errors: [] as string[],
    };

    // Collect unique missing entities from all valid rows
    const missingUsers = new Map<string, { name: string; email: string }>();
    const missingClients = new Map<string, string>();
    const missingProjects = new Map<string, string>(); // projectName -> clientName

    for (const item of preview) {
      if (item.errors.length > 0) continue;
      const entry = item.clockifyData;

      if (!item.mappedData.user_id && entry.Email) {
        missingUsers.set(entry.Email.toLowerCase(), {
          name: entry.User,
          email: entry.Email,
        });
      }
      if (entry.Client && !clients.find(
        (c) => c.name?.toLowerCase() === entry.Client.toLowerCase() ||
               c.company?.toLowerCase() === entry.Client.toLowerCase(),
      )) {
        missingClients.set(entry.Client.toLowerCase(), entry.Client);
      }
      if (!item.mappedData.project_id && entry.Project) {
        missingProjects.set(entry.Project.toLowerCase(), entry.Client || '');
      }
    }

    // 1. Create missing clients
    const createdClients: Record<string, number> = {};
    for (const entry of Array.from(missingClients.entries())) {
      const [key, name] = entry;
      try {
        const res = await clientsAPI.create({ name, company: name } as any);
        const newClient = res.data.client;
        createdClients[key] = newClient.id;
        setClients((prev) => [...prev, newClient]);
      } catch (error: any) {
        results.errors.push(`Failed to create client "${name}": ${error.response?.data?.details?.[0] || error.message}`);
      }
    }

    // 2. Create missing projects (linked to client if available, otherwise internal)
    const createdProjects: Record<string, number> = {};
    for (const entry of Array.from(missingProjects.entries())) {
      const [key, clientName] = entry;
      const originalName = preview.find(
        (p) => p.clockifyData.Project?.toLowerCase() === key,
      )?.clockifyData.Project || key;

      // Find client ID: check created clients first, then existing
      let clientId: number | null = null;
      if (clientName) {
        clientId = createdClients[clientName.toLowerCase()] || null;
        if (!clientId) {
          const existing = clients.find(
            (c) => c.name?.toLowerCase() === clientName.toLowerCase() ||
                   c.company?.toLowerCase() === clientName.toLowerCase(),
          );
          clientId = existing?.id || null;
        }
      }

      try {
        const projectData: any = {
          name: originalName,
          status: 'active',
          is_internal: !clientId,
        };
        if (clientId) projectData.client_id = clientId;

        const res = await projectsAPI.create(projectData);
        const newProject = res.data.project;
        createdProjects[key] = newProject.id;
        setProjects((prev) => [...prev, newProject]);
      } catch (error: any) {
        results.errors.push(`Failed to create project "${originalName}": ${error.response?.data?.details?.[0] || error.message}`);
      }
    }

    // 3. Create missing users (without sending invitation)
    const createdUsers: Record<string, number> = {};
    for (const entry of Array.from(missingUsers.entries())) {
      const [emailKey, userData] = entry;
      const nameParts = userData.name.trim().split(/\s+/);
      const firstName = nameParts[0] || 'Unknown';
      const lastName = nameParts.slice(1).join(' ') || 'User';

      try {
        const res = await api.post('/users/invite', {
          user: {
            email: userData.email,
            first_name: firstName,
            last_name: lastName,
            role: 'developer',
            employment_type: 'employee',
          },
          skip_invitation: true,
        });
        const newUser = res.data.user;
        createdUsers[emailKey] = newUser.id;
        setUsers((prev) => [...prev, newUser]);
      } catch (error: any) {
        results.errors.push(`Failed to create user "${userData.name}": ${error.response?.data?.details?.[0] || error.message}`);
      }
    }

    // 4. Import time entries with resolved IDs
    for (const previewItem of preview) {
      if (previewItem.errors.length > 0) {
        results.failed++;
        results.errors.push(
          `Row ${previewItem.rowIndex}: ${previewItem.errors.join(', ')}`,
        );
        continue;
      }

      const data = { ...previewItem.mappedData };
      const entry = previewItem.clockifyData;

      // Resolve user_id from created users if missing
      if (!data.user_id && entry.Email) {
        data.user_id = createdUsers[entry.Email.toLowerCase()];
      }

      // Resolve project_id from created projects if missing
      if (!data.project_id && entry.Project) {
        data.project_id = createdProjects[entry.Project.toLowerCase()];
      }

      if (!data.user_id || !data.project_id) {
        results.failed++;
        results.errors.push(
          `Row ${previewItem.rowIndex}: Could not resolve user or project`,
        );
        continue;
      }

      try {
        await timeEntriesAPI.create(data);
        results.successful++;
      } catch (error: any) {
        results.failed++;
        results.errors.push(
          `Row ${previewItem.rowIndex}: ${error.response?.data?.details?.[0] || 'Import failed'}`,
        );
      }
    }

    setImportResults(results);
    setImporting(false);
    setCurrentStep(3);
  };

  const validEntries = preview.filter((p) => p.errors.length === 0);
  const hasErrors = preview.some((p) => p.errors.length > 0);

  const cardClass = isDark
    ? 'bg-slate-900/50 border border-slate-700/50 rounded-2xl'
    : 'bg-white border border-slate-200 rounded-2xl shadow-sm';

  const selectClass = isDark
    ? 'w-full px-4 py-2.5 bg-slate-700/50 border border-slate-600 rounded-xl text-white focus:ring-2 focus:ring-violet-500 focus:border-transparent'
    : 'w-full px-4 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-violet-500 focus:border-transparent';

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
      <div
        className={`w-full max-w-6xl max-h-[90vh] overflow-hidden ${
          isDark ? 'bg-slate-900 border border-slate-700/50' : 'bg-white'
        } rounded-2xl shadow-xl`}
      >
        {/* Header */}
        <div
          className={`flex items-center justify-between p-6 border-b ${
            isDark ? 'border-slate-700/50' : 'border-slate-200'
          }`}
        >
          <h2
            className={`text-xl font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}
          >
            {t('clockifyImport.title')}
          </h2>
          <button
            onClick={onClose}
            className={`p-1 rounded-lg transition-colors ${
              isDark
                ? 'text-slate-400 hover:text-white hover:bg-slate-700/50'
                : 'text-slate-400 hover:text-slate-600 hover:bg-slate-100'
            }`}
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Progress Steps */}
        <div
          className={`flex items-center justify-center p-4 border-b ${
            isDark
              ? 'bg-slate-900/50 border-slate-700/50'
              : 'bg-slate-50 border-slate-200'
          }`}
        >
          <div className="flex items-center space-x-4">
            <div
              className={`flex items-center ${currentStep >= 1 ? (isDark ? 'text-violet-400' : 'text-violet-600') : isDark ? 'text-slate-500' : 'text-slate-400'}`}
            >
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  currentStep >= 1
                    ? 'bg-gradient-to-r from-violet-600 to-purple-600 text-white shadow-lg shadow-violet-500/25'
                    : isDark
                      ? 'bg-slate-700 text-slate-400'
                      : 'bg-slate-100'
                }`}
              >
                1
              </div>
              <span className="ml-2">{t('clockifyImport.uploadCsv')}</span>
            </div>
            <div
              className={`w-8 h-px ${isDark ? 'bg-slate-600' : 'bg-slate-300'}`}
            />
            <div
              className={`flex items-center ${currentStep >= 2 ? (isDark ? 'text-violet-400' : 'text-violet-600') : isDark ? 'text-slate-500' : 'text-slate-400'}`}
            >
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  currentStep >= 2
                    ? 'bg-gradient-to-r from-violet-600 to-purple-600 text-white shadow-lg shadow-violet-500/25'
                    : isDark
                      ? 'bg-slate-700 text-slate-400'
                      : 'bg-slate-100'
                }`}
              >
                2
              </div>
              <span className="ml-2">{t('clockifyImport.previewMap')}</span>
            </div>
            <div
              className={`w-8 h-px ${isDark ? 'bg-slate-600' : 'bg-slate-300'}`}
            />
            <div
              className={`flex items-center ${currentStep >= 3 ? (isDark ? 'text-violet-400' : 'text-violet-600') : isDark ? 'text-slate-500' : 'text-slate-400'}`}
            >
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  currentStep >= 3
                    ? 'bg-gradient-to-r from-violet-600 to-purple-600 text-white shadow-lg shadow-violet-500/25'
                    : isDark
                      ? 'bg-slate-700 text-slate-400'
                      : 'bg-slate-100'
                }`}
              >
                3
              </div>
              <span className="ml-2">{t('clockifyImport.importResults')}</span>
            </div>
          </div>
        </div>

        <div
          className="p-6 overflow-y-auto"
          style={{ maxHeight: 'calc(90vh - 200px)' }}
        >
          {/* Step 1: File Upload */}
          {currentStep === 1 && (
            <div className="space-y-6">
              <div className="text-center">
                <div
                  className={`border-2 border-dashed rounded-2xl p-8 transition-colors cursor-pointer ${
                    isDark
                      ? 'border-slate-600 hover:border-violet-500/50 bg-slate-900/30'
                      : 'border-slate-300 hover:border-violet-400'
                  }`}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <svg
                    className={`w-12 h-12 mx-auto mb-4 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                  <p
                    className={`text-lg font-medium mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}
                  >
                    {t('clockifyImport.uploadTitle')}
                  </p>
                  <p className={isDark ? 'text-slate-400' : 'text-slate-600'}>
                    {t('clockifyImport.uploadDescription')}
                  </p>
                  {file && (
                    <p
                      className={`text-sm mt-2 ${isDark ? 'text-violet-400' : 'text-violet-600'}`}
                    >
                      {t('clockifyImport.selected')}: {file.name}
                    </p>
                  )}
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  onChange={handleFileUpload}
                  className="hidden"
                />
              </div>

              {/* Configuration */}
              <div
                className={`p-4 rounded-xl ${isDark ? 'bg-slate-900/50 border border-slate-700/50' : 'bg-slate-50'}`}
              >
                <h3
                  className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}
                >
                  {t('clockifyImport.settings')}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label
                      className={`block text-sm font-medium mb-2 ${isDark ? 'text-slate-300' : 'text-slate-700'}`}
                    >
                      {t('clockifyImport.defaultProject')}
                    </label>
                    <select
                      value={mappingConfig.defaultProject}
                      onChange={(e) =>
                        setMappingConfig((prev) => ({
                          ...prev,
                          defaultProject: e.target.value,
                        }))
                      }
                      className={selectClass}
                    >
                      <option value="">
                        {t('clockifyImport.selectProjectForUnmapped')}
                      </option>
                      {projects.map((project) => (
                        <option key={project.id} value={project.id}>
                          {project.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label
                      className={`block text-sm font-medium mb-2 ${isDark ? 'text-slate-300' : 'text-slate-700'}`}
                    >
                      {t('clockifyImport.defaultCategory')}
                    </label>
                    <select
                      value={mappingConfig.defaultCategory}
                      onChange={(e) =>
                        setMappingConfig((prev) => ({
                          ...prev,
                          defaultCategory: e.target.value,
                        }))
                      }
                      className={selectClass}
                    >
                      {categories.map((category) => (
                        <option key={category.id} value={category.id}>
                          {category.name} ({category.code})
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="mt-4 space-y-2">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={mappingConfig.userEmailMapping}
                      onChange={(e) =>
                        setMappingConfig((prev) => ({
                          ...prev,
                          userEmailMapping: e.target.checked,
                        }))
                      }
                      className="rounded border-slate-300 text-violet-600 focus:ring-violet-500"
                    />
                    <span
                      className={`ml-2 text-sm ${isDark ? 'text-slate-300' : 'text-slate-700'}`}
                    >
                      {t('clockifyImport.mapByEmail')}
                    </span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={mappingConfig.billableByDefault}
                      onChange={(e) =>
                        setMappingConfig((prev) => ({
                          ...prev,
                          billableByDefault: e.target.checked,
                        }))
                      }
                      className="rounded border-slate-300 text-violet-600 focus:ring-violet-500"
                    />
                    <span
                      className={`ml-2 text-sm ${isDark ? 'text-slate-300' : 'text-slate-700'}`}
                    >
                      {t('clockifyImport.billableByDefault')}
                    </span>
                  </label>
                </div>
              </div>

              {/* Expected Format Info */}
              <div
                className={`p-4 rounded-xl ${
                  isDark
                    ? 'bg-violet-500/10 border border-violet-500/30'
                    : 'bg-blue-50 border border-blue-100'
                }`}
              >
                <h4
                  className={`text-sm font-medium mb-2 ${isDark ? 'text-violet-300' : 'text-blue-900'}`}
                >
                  {t('clockifyImport.expectedFormat')}
                </h4>
                <p
                  className={`text-sm mb-2 ${isDark ? 'text-violet-200' : 'text-blue-800'}`}
                >
                  {t('clockifyImport.expectedFormatDescription')}
                </p>
                <div
                  className={`text-xs p-2 rounded-lg font-mono ${
                    isDark
                      ? 'bg-violet-500/20 text-violet-300'
                      : 'bg-blue-100 text-blue-700'
                  }`}
                >
                  Project, Description, Task, User, Email, Start Date, Start
                  Time, End Date, End Time, Duration (h), Duration (decimal)
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Preview */}
          {currentStep === 2 && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3
                    className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}
                  >
                    {t('clockifyImport.importPreview')}
                  </h3>
                  <p
                    className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-600'}`}
                  >
                    {t('clockifyImport.foundEntries', {
                      total: csvData.length,
                      valid: validEntries.length,
                      errors: preview.length - validEntries.length,
                    })}
                  </p>
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() => setCurrentStep(1)}
                    className={`px-5 py-2.5 text-sm font-medium rounded-xl transition-colors ${
                      isDark
                        ? 'text-slate-300 hover:text-white hover:bg-slate-700/50'
                        : 'border border-slate-300 text-slate-700 hover:bg-slate-50'
                    }`}
                  >
                    {t('common.back')}
                  </button>
                  <button
                    onClick={performImport}
                    disabled={validEntries.length === 0}
                    className="px-5 py-2.5 bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-500 hover:to-purple-500 text-white text-sm font-semibold rounded-xl shadow-lg shadow-violet-500/25 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {t('clockifyImport.importEntries', {
                      count: validEntries.length,
                    })}
                  </button>
                </div>
              </div>

              {/* Preview Table */}
              <div
                className={`overflow-x-auto rounded-xl ${
                  isDark
                    ? 'border border-slate-700/50'
                    : 'border border-slate-200'
                }`}
              >
                <table className="w-full">
                  <thead className={isDark ? 'bg-slate-700/30' : 'bg-slate-50'}>
                    <tr>
                      <th
                        className={`px-4 py-3 text-left text-xs font-semibold uppercase ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
                      >
                        {t('clockifyImport.row')}
                      </th>
                      <th
                        className={`px-4 py-3 text-left text-xs font-semibold uppercase ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
                      >
                        {t('clockifyImport.user')}
                      </th>
                      <th
                        className={`px-4 py-3 text-left text-xs font-semibold uppercase ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
                      >
                        {t('clockifyImport.project')}
                      </th>
                      <th
                        className={`px-4 py-3 text-left text-xs font-semibold uppercase ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
                      >
                        {t('clockifyImport.date')}
                      </th>
                      <th
                        className={`px-4 py-3 text-left text-xs font-semibold uppercase ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
                      >
                        {t('clockifyImport.hours')}
                      </th>
                      <th
                        className={`px-4 py-3 text-left text-xs font-semibold uppercase ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
                      >
                        {t('clockifyImport.description')}
                      </th>
                      <th
                        className={`px-4 py-3 text-left text-xs font-semibold uppercase ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
                      >
                        {t('clockifyImport.status')}
                      </th>
                    </tr>
                  </thead>
                  <tbody
                    className={
                      isDark
                        ? 'divide-y divide-slate-700/50'
                        : 'divide-y divide-slate-200'
                    }
                  >
                    {preview.slice(0, 50).map((item, index) => (
                      <tr
                        key={index}
                        className={
                          item.errors.length > 0
                            ? isDark
                              ? 'bg-rose-500/10'
                              : 'bg-red-50'
                            : item.warnings.length > 0
                              ? isDark
                                ? 'bg-amber-500/10'
                                : 'bg-yellow-50'
                              : ''
                        }
                      >
                        <td
                          className={`px-4 py-3 text-sm ${isDark ? 'text-white' : 'text-slate-900'}`}
                        >
                          {item.rowIndex}
                        </td>
                        <td
                          className={`px-4 py-3 text-sm ${isDark ? 'text-white' : 'text-slate-900'}`}
                        >
                          {item.clockifyData.User}
                        </td>
                        <td
                          className={`px-4 py-3 text-sm ${isDark ? 'text-white' : 'text-slate-900'}`}
                        >
                          {item.clockifyData.Project}
                        </td>
                        <td
                          className={`px-4 py-3 text-sm ${isDark ? 'text-white' : 'text-slate-900'}`}
                        >
                          {item.clockifyData['Start Date']}
                        </td>
                        <td
                          className={`px-4 py-3 text-sm ${isDark ? 'text-white' : 'text-slate-900'}`}
                        >
                          {item.mappedData.hours_worked?.toFixed(2)}
                        </td>
                        <td
                          className={`px-4 py-3 text-sm max-w-xs truncate ${isDark ? 'text-white' : 'text-slate-900'}`}
                        >
                          {item.mappedData.description}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          {item.errors.length > 0 ? (
                            <span
                              className={`font-medium ${isDark ? 'text-rose-400' : 'text-red-600'}`}
                            >
                              {t('clockifyImport.error')}
                            </span>
                          ) : item.warnings.length > 0 ? (
                            <span
                              className={`font-medium ${isDark ? 'text-amber-400' : 'text-yellow-600'}`}
                            >
                              {t('clockifyImport.warning')}
                            </span>
                          ) : (
                            <span
                              className={`font-medium ${isDark ? 'text-emerald-400' : 'text-green-600'}`}
                            >
                              {t('clockifyImport.ready')}
                            </span>
                          )}
                          {(item.errors.length > 0 ||
                            item.warnings.length > 0) && (
                            <div className="text-xs mt-1">
                              {item.errors.map((error, i) => (
                                <div
                                  key={i}
                                  className={
                                    isDark ? 'text-rose-400' : 'text-red-600'
                                  }
                                >
                                  {error}
                                </div>
                              ))}
                              {item.warnings.map((warning, i) => (
                                <div
                                  key={i}
                                  className={
                                    isDark
                                      ? 'text-amber-400'
                                      : 'text-yellow-600'
                                  }
                                >
                                  {warning}
                                </div>
                              ))}
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {preview.length > 50 && (
                  <div
                    className={`px-4 py-3 text-sm ${
                      isDark
                        ? 'bg-slate-700/30 text-slate-400'
                        : 'bg-slate-50 text-slate-600'
                    }`}
                  >
                    {t('clockifyImport.showingFirst', {
                      shown: 50,
                      remaining: preview.length - 50,
                    })}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Step 3: Results */}
          {currentStep === 3 && importResults && (
            <div className="space-y-6">
              <div className="text-center">
                <div
                  className={`w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center ${
                    importResults.failed === 0
                      ? isDark
                        ? 'bg-emerald-500/20'
                        : 'bg-green-100'
                      : isDark
                        ? 'bg-amber-500/20'
                        : 'bg-yellow-100'
                  }`}
                >
                  {importResults.failed === 0 ? (
                    <svg
                      className={`w-8 h-8 ${isDark ? 'text-emerald-400' : 'text-green-600'}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  ) : (
                    <svg
                      className={`w-8 h-8 ${isDark ? 'text-amber-400' : 'text-yellow-600'}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                      />
                    </svg>
                  )}
                </div>
                <h3
                  className={`text-xl font-semibold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}
                >
                  {t('clockifyImport.importComplete')}
                </h3>
                <p className={isDark ? 'text-slate-400' : 'text-slate-600'}>
                  {t('clockifyImport.importSuccessful', {
                    count: importResults.successful,
                  })}
                  {importResults.failed > 0 &&
                    `, ${t('clockifyImport.importFailed', { count: importResults.failed })}`}
                </p>
              </div>

              {/* Results Summary */}
              <div className="grid grid-cols-2 gap-4">
                <div
                  className={`p-4 rounded-xl text-center ${
                    isDark ? 'bg-emerald-500/20' : 'bg-green-50'
                  }`}
                >
                  <div
                    className={`text-2xl font-bold ${isDark ? 'text-emerald-400' : 'text-green-600'}`}
                  >
                    {importResults.successful}
                  </div>
                  <div
                    className={`text-sm ${isDark ? 'text-emerald-300' : 'text-green-800'}`}
                  >
                    {t('clockifyImport.successful')}
                  </div>
                </div>
                <div
                  className={`p-4 rounded-xl text-center ${
                    isDark ? 'bg-rose-500/20' : 'bg-red-50'
                  }`}
                >
                  <div
                    className={`text-2xl font-bold ${isDark ? 'text-rose-400' : 'text-red-600'}`}
                  >
                    {importResults.failed}
                  </div>
                  <div
                    className={`text-sm ${isDark ? 'text-rose-300' : 'text-red-800'}`}
                  >
                    {t('clockifyImport.failed')}
                  </div>
                </div>
              </div>

              {/* Errors */}
              {importResults.errors.length > 0 && (
                <div
                  className={`rounded-xl p-4 ${
                    isDark
                      ? 'bg-rose-500/10 border border-rose-500/30'
                      : 'bg-red-50 border border-red-200'
                  }`}
                >
                  <h4
                    className={`font-medium mb-2 ${isDark ? 'text-rose-300' : 'text-red-800'}`}
                  >
                    {t('clockifyImport.importErrors')}:
                  </h4>
                  <div className="max-h-48 overflow-y-auto">
                    {importResults.errors.map(
                      (error: string, index: number) => (
                        <div
                          key={index}
                          className={`text-sm mb-1 ${isDark ? 'text-rose-400' : 'text-red-700'}`}
                        >
                          {error}
                        </div>
                      ),
                    )}
                  </div>
                </div>
              )}

              <div className="flex justify-center">
                <button
                  onClick={() => {
                    onImportComplete();
                    onClose();
                  }}
                  className="px-6 py-2.5 bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-500 hover:to-purple-500 text-white text-sm font-semibold rounded-xl shadow-lg shadow-violet-500/25 transition-all duration-200"
                >
                  {t('common.done')}
                </button>
              </div>
            </div>
          )}

          {/* Loading Spinner */}
          {importing && (
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
              <div
                className={`p-6 rounded-2xl text-center ${
                  isDark ? 'bg-slate-900 border border-slate-700' : 'bg-white'
                } shadow-xl`}
              >
                <div className="w-12 h-12 border-2 border-violet-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                <p className={isDark ? 'text-white' : 'text-slate-700'}>
                  {t('clockifyImport.importing')}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ClockifyImport;
